const PERIOD_ALLOCATION_TYPES = {
  day: 'none',
  month: 'specified_period',
};

const PERIOD_TYPES = new Set(['day', 'month']);

const SOURCE_ALLOCATION_TYPES = {
  expense: new Set([
    'none',
    'specified_period',
    'payment_to_month_end',
    'previous_month',
    'current_month',
    'next_month',
    'rental_next_month',
  ]),
  income: new Set(['none']),
  plan: new Set(['specified_period']),
  salary: new Set(['specified_period', 'next_month']),
};

const MONTH_PERIOD_ALLOCATION_TYPES = new Set([
  'specified_period',
  'previous_month',
  'current_month',
  'next_month',
  'rental_next_month',
]);

export const getMetricPeriodAllocationType = (periodType) =>
  PERIOD_ALLOCATION_TYPES[periodType] || null;

export const isMetricAllocationTypeSupported = (sourceDomain, allocationType) =>
  !sourceDomain || Boolean(SOURCE_ALLOCATION_TYPES[sourceDomain]?.has(allocationType));

const isMetricPeriodAllocationCompatible = ({ allocationType, periodType, valueType }) => {
  if (!periodType) {
    return true;
  }
  if (periodType === 'day') {
    return valueType === 'ratio' ? allocationType == null : allocationType === 'none';
  }
  return (
    periodType === 'month' &&
    (allocationType == null || MONTH_PERIOD_ALLOCATION_TYPES.has(allocationType))
  );
};

export const getMetricLifecycleValidationError = ({
  allocationType,
  metricType,
  periodType,
  sourceDomain,
  statisticalModelType,
  valueType,
} = {}) => {
  if (metricType === 'composite') {
    return allocationType || periodType || statisticalModelType
      ? '复合指标不能配置生命周期或推测模型'
      : null;
  }
  if (metricType !== 'atomic') {
    return null;
  }
  if (periodType && !PERIOD_TYPES.has(periodType)) {
    return '指标周期不正确';
  }
  if (valueType === 'ratio') {
    if (!periodType) {
      return '比率原子指标必须配置指标周期';
    }
  } else if (!allocationType) {
    return '金额或数量原子指标必须配置生效策略';
  }
  if (allocationType && !isMetricAllocationTypeSupported(sourceDomain, allocationType)) {
    return '当前来源领域不支持该生效策略';
  }
  if (statisticalModelType && !periodType) {
    return '推测模型要求配置指标周期';
  }
  if (isMetricPeriodAllocationCompatible({ allocationType, periodType, valueType })) {
    return null;
  }
  const periodLabel = { day: '日', month: '月' }[periodType];
  return `${periodLabel}周期与当前生效策略不兼容`;
};

export const getMetricBatchLifecycleValidationError = ({ fields = [], ...values } = {}) => {
  if (!fields.includes('allocationType') || !fields.includes('period')) {
    return null;
  }
  return getMetricLifecycleValidationError({
    ...values,
    metricType: 'atomic',
    sourceDomain: 'expense',
    valueType: 'amount',
  });
};

export const getMetricLifecycleChangeValues = ({ changedValues = {}, currentValues = {} } = {}) => {
  const lifecycleValues = {};
  const periodChanged = Object.hasOwn(changedValues, 'periodType');
  const valueTypeChanged = Object.hasOwn(changedValues, 'valueType');
  if (periodChanged && !changedValues.periodType) {
    lifecycleValues.statisticalModelType = null;
  }
  const periodType = periodChanged ? changedValues.periodType : currentValues.periodType;
  const valueType = valueTypeChanged ? changedValues.valueType : currentValues.valueType;
  if ((periodChanged || valueTypeChanged) && periodType === 'day' && valueType === 'ratio') {
    lifecycleValues.allocationType = null;
  }
  return lifecycleValues;
};

export const normalizeMetricBatchLifecyclePatch = (values = {}) => {
  const hasAllocationType = Object.hasOwn(values, 'allocationType');
  const hasPeriodType = Object.hasOwn(values, 'periodType');
  if (hasPeriodType) {
    const periodType = values.periodType || null;
    return {
      ...(hasAllocationType ? { allocationType: values.allocationType } : {}),
      periodType,
      statisticalModelType: periodType ? (values.statisticalModelType ?? null) : null,
    };
  }
  if (hasAllocationType) {
    return { allocationType: values.allocationType };
  }
  return Object.hasOwn(values, 'statisticalModelType')
    ? { statisticalModelType: values.statisticalModelType ?? null }
    : {};
};

export const normalizeMetricBatchLifecycleValues = ({ fields = [], ...values } = {}) => {
  const selectedValues = {};
  if (fields.includes('allocationType')) {
    selectedValues.allocationType = values.allocationType;
  }
  if (fields.includes('period')) {
    selectedValues.periodType = values.periodType;
    selectedValues.statisticalModelType = values.statisticalModelType;
  }
  return normalizeMetricBatchLifecyclePatch(selectedValues);
};
